import { useCallback, useEffect, useRef, useMemo } from 'react';
import AvaturnService from '@features/avaturn/services/AvaturnService';
import AvaturnServiceEk from '@clients/ek/features/avaturn/services/AvaturnService';
import { getUseAvaturnHelper } from '@features/avatar/helpers';
import { MytaverseLogger } from '@helpers/logger';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useMytaverseEvent } from '@core/dashboard/providers';
import {
  ChangeAvatarTypes,
  IXRHrPersonaTypes,
  WebsocketAction,
} from '@clients/ek/interfaces';
import ParticipantsService from '@services/ParticipantsService';
import { AvatarTypes } from '@interfaces/avatar';
import { IRecreateAvaturnAvatar } from '@core/dashboard/providers/MytaverseEventProvider/interfaces';
import { AvaturnStatuses } from '@features/avaturn/interfaces';
import { getCatchErrorMessage } from '@helpers/error';
import { getNotification } from '@components/Notification/helpers';
import { NOTIFICATION_TYPES, useNotification } from '@components/Notification';
import { useTranslation } from 'react-i18next';
import { IUser } from '@interfaces/user';

const useAvaturnHelper = getUseAvaturnHelper();

interface IAvatarInfo {
  avatarId: string;
  avaturnId: string;
  avatarUrl: string;
}

const useMagicMirror = () => {
  const { getAssetParams, getProcessedBrand, deleteAvatar } =
    useAvaturnHelper();

  const {
    setCurrentAvatarId,
    setCustomAvatarUrl,
    user,
    setUser,
    sendMessageToUnreal,
  } = useMytaverse();
  const { recreateAvaturnAvatar, setRecreateAvaturnAvatar } =
    useMytaverseEvent();

  const { showNotification } = useNotification();
  const { t: translate } = useTranslation();

  const processedBrand = getProcessedBrand();
  const persona = user?.hrData?.persona.toUpperCase();

  const timeoutIdRef = useRef<NodeJS.Timeout | number | null>(null);

  const halfUniformRef = useRef<IAvatarInfo | null>(null);
  const fullUniformRef = useRef<IAvatarInfo | null>(null);

  const cleanUniformedAvatars = useCallback(
    async (avatarId: string) => {
      try {
        const avatars = await ParticipantsService.getParticipantAvatars();

        //keep only one uniformed avatar
        for (const avatar of avatars) {
          if (
            avatar.avatarType === AvatarTypes.AVATURN &&
            avatar.tags?.includes('UNIFORM') &&
            avatar.avatarId !== avatarId
          ) {
            await deleteAvatar(avatar.avatarId, processedBrand);
          }
        }
      } catch (e) {
        MytaverseLogger.log(`MM remove Avatars: ${getCatchErrorMessage(e)}`);
      }
    },
    [processedBrand],
  );

  const hasUniformed = useMemo(() => {
    return !!user?.avatars.find(({ tags = [] }) => tags.includes('UNIFORM'));
  }, [user?.avatars]);

  const cleanLimitedAvatars = useCallback(async () => {
    try {
      const avatars = await ParticipantsService.getParticipantAvaturnAvatars();

      for (const avatar of avatars) {
        if (avatar.status === AvaturnStatuses.LIMITED) {
          await ParticipantsService.deleteParticipantAvatarFromList({
            avatarType: AvatarTypes.AVATURN,
            avatarId: avatar.avatarId,
            brand: processedBrand,
            hardDelete: true,
          });
        }
      }
    } catch (e) {
      MytaverseLogger.log(`MM clean Avatars: ${getCatchErrorMessage(e)}`);
    }
  }, [processedBrand]);

  const abinitioCleanAvatars = useCallback(
    async (persona: string) => {
      if (persona !== IXRHrPersonaTypes.Abinitio) {
        return;
      }

      try {
        const avatars = await ParticipantsService.getParticipantAvatars();

        for (const avatar of avatars) {
          if (
            avatar.avatarType === AvatarTypes.AVATURN &&
            avatar.tags?.includes('NON_UNIFORM')
          ) {
            await deleteAvatar(avatar.avatarId, processedBrand);
          }
        }
      } catch (e) {
        MytaverseLogger.log(
          `MM remove Abinitio Avatars: ${getCatchErrorMessage(e)}`,
        );
      }
    },
    [processedBrand],
  );

  const fetchAvatarInfo = useCallback(async (avaturnId: string) => {
    let avatarUrl;
    try {
      const data = await AvaturnService.getAvatarInfo(avaturnId);
      if (
        data?.status &&
        [
          AvaturnStatuses.FAILED_CREATION,
          AvaturnStatuses.FAILED_EXPORTING,
          AvaturnStatuses.FAILED,
        ].includes(data.status)
      ) {
        throw new Error('Cannot create an avatar');
      } else if (data.avatarUrl) {
        avatarUrl = data.avatarUrl;

        fullUniformRef.current = {
          avatarId: data.avatarId,
          avaturnId: data.avaturnId,
          avatarUrl: data.avatarUrl as string,
        };

        await cleanUniformedAvatars(data.avatarId);

        if (halfUniformRef.current) {
          setCustomAvatarUrl(halfUniformRef.current.avatarUrl);
          setCurrentAvatarId(halfUniformRef.current.avatarId);
        }
      }
    } catch (e) {
      MytaverseLogger.log(`fetchAvatarInfo: ${getCatchErrorMessage(e)}`);

      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }

      showNotification(
        getNotification({
          message: translate('notifications.exportAvaturnError'),
          type: NOTIFICATION_TYPES.ERROR,
          withReloadButton: false,
          withDismissButton: false,
          closeManually: true,
        }),
      );

      return;
    }

    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }

    if (!avatarUrl) {
      timeoutIdRef.current = setTimeout(() => {
        fetchAvatarInfo(avaturnId);
      }, 5000);
    }
  }, []);

  const generateUniformAvatar = useCallback(
    async ({ avatarId, avaturnId, url, gender }: IRecreateAvaturnAvatar) => {
      const processedBrand = getProcessedBrand();

      // when all data is ready we need to save it to our db and s3
      // save generated avatar HALF_UNIFORM LIMITED
      const exportedAvaturn = await AvaturnService.exportAvaturnDataToS3(
        processedBrand,
        {
          url,
          gender,
          avatarId,
          avaturnId,
        },
      );

      if (hasUniformed) {
        MytaverseLogger.log('Subsequent MM');

        //If user has uniformed (subsequent login), generate only one
        setCustomAvatarUrl(exportedAvaturn.avatarUrl as string);
        setCurrentAvatarId(exportedAvaturn.avatarId);

        await ParticipantsService.addParticipantAvatarToList({
          avatarType: AvatarTypes.AVATURN,
          avatarId: avatarId,
        });
        await Promise.all([
          AvaturnService.deleteAvaturn(avaturnId || ''),
          cleanUniformedAvatars(exportedAvaturn.avatarId),
        ]);

        return;
      }

      halfUniformRef.current = {
        avatarId: exportedAvaturn.avatarId,
        avaturnId: exportedAvaturn.avaturnId,
        avatarUrl: exportedAvaturn.avatarUrl as string,
      };

      await Promise.all([
        AvaturnService.updateAvatar(avaturnId, {
          status: AvaturnStatuses.LIMITED,
          tags: [
            ...(exportedAvaturn.tags || []),
            ChangeAvatarTypes.UniformHalf,
          ],
        }),
        ParticipantsService.addParticipantAvatarToList({
          avatarType: AvatarTypes.AVATURN,
          avatarId: avatarId,
        }),
        // AvaturnService.deleteAvaturn(avaturnId || ''),
      ]);

      // save generated avatar FULL_UNIFORM ACTIVE
      const params = getAssetParams(true, ChangeAvatarTypes.UniformFull);

      //TODO: remove magicMirror endpoint
      const activeAvatar = await AvaturnService.cloneAvatar({
        avatarId: exportedAvaturn.avatarId,
        brand: params.brand,
        ixrCredentialStatus: params.credentialStatus,
        gender: params.gender,
        persona: `${params.persona}|${ChangeAvatarTypes.UniformFull}`,
        renderPic: true,
      });

      await ParticipantsService.addParticipantAvatarToList({
        avatarType: AvatarTypes.AVATURN,
        avatarId: activeAvatar.avatarId,
      });

      fetchAvatarInfo(activeAvatar.avaturnId);
    },
    [fetchAvatarInfo, getAssetParams, hasUniformed],
  );

  const crowningMoment = useCallback(async () => {
    if (!fullUniformRef.current) {
      return;
    }

    setCustomAvatarUrl(fullUniformRef.current.avatarUrl);
    setCurrentAvatarId(fullUniformRef.current.avatarId);
  }, []);

  const saveUniformedAvatar = useCallback(async () => {
    if (!fullUniformRef.current) {
      return;
    }

    //make it active only after crowning moment
    await AvaturnService.updateAvatar(fullUniformRef.current.avaturnId, {
      status: AvaturnStatuses.ACTIVE,
    });

    sendMessageToUnreal({ action: WebsocketAction.CmAvatarProcessed });

    await ParticipantsService.updateCurrentParticipantAvatar({
      activeAvatarId: fullUniformRef.current.avatarId,
    });

    //remove UNIFORM_HALF
    if (halfUniformRef?.current) {
      // await ParticipantsService.deleteParticipantAvatarFromList({
      //   avatarType: AvatarTypes.AVATURN,
      //   avatarId: halfUniformRef.current.avatarId,
      //   brand: processedBrand,
      //   hardDelete: true,
      // });

      halfUniformRef.current = null;
    }

    fullUniformRef.current = null;

    //Remove non_uniformed avatars for Abinitio
    await abinitioCleanAvatars(persona);

    //Clean all limited avatars
    //await cleanLimitedAvatars();

    const avatars = await ParticipantsService.getParticipantAvatars();
    setUser((prevState: IUser) => ({ ...prevState, avatars }));
  }, [persona, processedBrand, abinitioCleanAvatars, sendMessageToUnreal]);

  useEffect(() => {
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!recreateAvaturnAvatar) {
      return;
    }

    generateUniformAvatar(recreateAvaturnAvatar);
    setRecreateAvaturnAvatar(null);
  }, [recreateAvaturnAvatar]);

  return { crowningMoment, saveUniformedAvatar };
};

export default useMagicMirror;
